import { z } from "zod";
import { CURRENT_POSITION_ELIGIBLE_CLASSES, EPISTEMIC_CLASSES, EPISTEMIC_DISCIPLINE, checkPositionSentence, renderEntrySentence, renderEpistemicSections, selectEpistemicEntries, } from "./epistemic-selection.js";
import { claimCitationId } from "./epistemic.js";
import { connectedContentIdentityInput, connectedDecisionCitationId, connectedFactCitationId, } from "./subject-connected-knowledge.js";
import { factsAreCompeting, isOpenQuestionResolved } from "./subject-intelligence.js";
export const CURRENT_POSITION_MAX_SENTENCES = 8;
export const CURRENT_POSITION_TARGET_SENTENCES = 5;
export const CurrentPositionStatusSchema = z.enum(["sufficient", "insufficient"]);
export const CurrentPositionOriginSchema = z.enum(["skeleton", "generated"]);
export const CurrentPositionItemSchema = z.object({
    text: z.string().min(1).max(800),
    citationIds: z.array(z.string().min(1)).max(12),
});
export const CurrentPositionModelOutputSchema = z.object({
    sentences: z.array(CurrentPositionItemSchema).max(CURRENT_POSITION_MAX_SENTENCES),
});
export const CurrentPositionResultSchema = z.object({
    status: CurrentPositionStatusSchema,
    origin: CurrentPositionOriginSchema,
    generatedAt: z.string().datetime(),
    subjectId: z.string().min(1),
    bundleVersion: z.literal(1),
    bundleIdentity: z.string().min(1),
    truncated: z.boolean(),
    items: z.array(CurrentPositionItemSchema),
    insufficientReason: z.string().nullable(),
});
export const SubjectCurrentPositionQuerySchema = z.object({
    refresh: z.enum(["1", "true"]).optional(),
});
export const CURRENT_POSITION_DISCIPLINE = [
    "CURRENT POSITION DISCIPLINE:",
    "Write 3–5 concise sentences answering where matters currently stand on this Subject, keeping every record at exactly the epistemic strength its class gives it.",
    "Do not restate the Subject identity summary. Prefer the organisation's explicitly adopted positions and decisions, agreements, verified observations and counterparty positions; then material qualifications and unresolved matters.",
    "Only records under ORGANISATION POSITION, AGREED, VERIFIED OBSERVATIONS or COUNTERPARTY POSITIONS may be written as present position. Canonical storage, acceptance or publication of a record is NOT adoption, agreement, verification or a counterparty statement.",
    "Everything else keeps its frame in your sentence: “The draft states …”, “It is proposed that …”, “The working assumption is …”, “Under the scenario …”, “It remains unresolved whether …”, “A recorded value (unclassified) is …”.",
    "Do not list facts merely because they exist. Use only the supplied Subject context. Do not speculate. Do not invent facts, prices, terms, settlement, risk conclusions or resolutions.",
    "Records marked recorded-on=<origin> are recorded on that named origin Subject; name the origin once when you use them and never present them as recorded on this Subject.",
    "Present later asOf values as current; do not describe historical same-key values as present. If competing values share a period, say they remain unresolved. Do not pick a winner.",
    "Do not treat an open question as answered. Do not infer CRM identity from names.",
    "Each sentence MUST include citationIds drawn from the supplied [citationId] tokens. Sentences that overstate their cited records are discarded.",
    'Return JSON only: {"sentences":[{"text":"...","citationIds":["..."]}]}',
].join(" ");
function asOfDay(value) {
    if (!value?.trim())
        return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(value))
        return value.slice(0, 10);
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed))
        return null;
    return new Date(parsed).toISOString().slice(0, 10);
}
function fnv1aHex(input) {
    let hash = 2166136261;
    for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}
function clip(text, max) {
    const trimmed = text.trim();
    if (trimmed.length <= max)
        return trimmed;
    return `${trimmed.slice(0, Math.max(0, max - 1))}…`;
}
export function selectCurrentFacts(facts) {
    const byKey = new Map();
    for (const fact of facts) {
        if (fact.superseded)
            continue;
        const list = byKey.get(fact.key) ?? [];
        list.push(fact);
        byKey.set(fact.key, list);
    }
    const out = [];
    for (const [key, group] of [...byKey.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        const days = group.map((row) => asOfDay(row.asOf)).filter((day) => Boolean(day));
        const latestDay = days.sort().at(-1) ?? null;
        const current = latestDay ? group.filter((row) => asOfDay(row.asOf) === latestDay) : group;
        let competing = false;
        for (let i = 0; i < current.length; i += 1) {
            for (let j = i + 1; j < current.length; j += 1) {
                const left = current[i];
                const right = current[j];
                if (left && right && factsAreCompeting(left, right))
                    competing = true;
            }
        }
        out.push({
            key,
            competing,
            facts: current.map((row) => ({
                citationId: row.citationId,
                value: row.value,
                unit: row.unit ?? null,
                asOf: row.asOf,
            })),
        });
    }
    return out;
}
/**
 * Deterministic epistemic selection for the pinned Subject (ADR-0117). Runs
 * before any prose generation. Current facts follow the ordinary currency
 * rule (latest asOf per key); explicit claims bound to a current fact replace
 * its legacy entry; everything unbound stays `unclassified-legacy`.
 */
export function selectSubjectEpistemic(bundle) {
    const byCitation = new Map(bundle.canonical.facts.map((fact) => [fact.citationId, fact]));
    const facts = selectCurrentFacts(bundle.canonical.facts).flatMap((group) => group.facts.map((row) => {
        const full = byCitation.get(row.citationId);
        return {
            citationId: row.citationId,
            key: group.key,
            value: row.value,
            unit: row.unit,
            source: full?.source ?? "",
            asOf: row.asOf,
            note: full?.note ?? null,
            competing: group.competing,
        };
    }));
    for (const origin of bundle.connected.origins) {
        for (const fact of origin.facts) {
            facts.push({
                citationId: connectedFactCitationId(origin.originSubjectId, fact.citationId),
                key: fact.key,
                value: fact.value,
                unit: fact.unit ?? null,
                source: fact.source,
                asOf: fact.asOf,
                note: null,
                originLabel: origin.label,
            });
        }
    }
    const selection = selectEpistemicEntries({
        facts,
        claims: bundle.epistemic?.claims ?? [],
        lineage: bundle.epistemic?.lineage ?? [],
        decisions: bundle.canonical.decisions.map((row) => ({
            citationId: row.citationId,
            text: row.text,
            at: row.at,
        })),
        openQuestions: unresolvedOpenQuestions(bundle).map((row) => ({
            citationId: row.citationId,
            label: row.label,
        })),
    });
    // Connected decisions are recorded on their origin; same class, origin kept.
    for (const origin of bundle.connected.origins) {
        for (const decision of origin.decisions) {
            const citationId = connectedDecisionCitationId(origin.originSubjectId, decision.citationId);
            const entry = {
                class: "recorded-decision",
                state: "machine-classified",
                citationIds: [citationId],
                proposition: decision.text,
                claimType: "decision",
                factKey: null,
                source: null,
                asOf: decision.at.slice(0, 10),
                note: null,
                originLabel: origin.label,
                authority: null,
                attribution: { actorLabel: null, actorKind: null, status: "none" },
                verification: null,
                selfStance: null,
                externalStances: [],
                evidence: [],
                derived: false,
                origin: null,
                inferenceMode: null,
                condition: null,
                conditional: false,
                lineage: [],
                competing: false,
                at: decision.at,
            };
            selection.entries.push(entry);
            selection.byCitation.set(citationId, entry);
        }
    }
    return selection;
}
export const CURRENT_POSITION_INSUFFICIENT_UNCLASSIFIED = "No explicitly adopted, agreed or verified position is recorded. The records below are listed as recorded — they are not presented as the organisation's position.";
/**
 * Sufficient only when an ELIGIBLE present position exists: an explicit
 * organisation `adopted` stance, an agreement, a verified observation, or a
 * directly attributed counterparty position (or the Subject itself is
 * superseded). Canonical or accepted facts alone are not a position.
 */
export function assessCurrentPositionSufficiency(bundle, selection = selectSubjectEpistemic(bundle)) {
    if (bundle.canonical.superseded)
        return { status: "sufficient", reason: null };
    if (selection.eligible.length > 0)
        return { status: "sufficient", reason: null };
    const nonQuestion = selection.entries.filter((entry) => entry.class !== "open-question");
    if (nonQuestion.length > 0 ||
        bundle.canonical.body.text.trim() ||
        bundle.canonical.summary.text.trim()) {
        return { status: "insufficient", reason: CURRENT_POSITION_INSUFFICIENT_UNCLASSIFIED };
    }
    if (selection.entries.some((entry) => entry.class === "open-question")) {
        return {
            status: "insufficient",
            reason: "Only unresolved questions are recorded; there is no present position.",
        };
    }
    return {
        status: "insufficient",
        reason: "Only identity or operational metadata is recorded; there is no present position.",
    };
}
export function collectCurrentPositionCitationIds(bundle) {
    const ids = new Set();
    const add = (id) => {
        if (id)
            ids.add(id);
    };
    const canonical = bundle.canonical;
    add(canonical.identity.citationId);
    add(canonical.summary.citationId);
    add(canonical.body.citationId);
    for (const fact of canonical.facts)
        add(fact.citationId);
    for (const decision of canonical.decisions)
        add(decision.citationId);
    for (const question of canonical.openQuestions)
        add(question.citationId);
    for (const claim of bundle.epistemic?.claims ?? [])
        add(claimCitationId(claim.id));
    for (const evidence of canonical.evidence) {
        if (evidence.unavailableReason === "restricted")
            continue;
        add(evidence.citationId);
    }
    for (const excerpt of bundle.retrieval.excerpts) {
        if (excerpt.layer === "association")
            continue;
        if (excerpt.unavailableReason === "restricted")
            continue;
        add(excerpt.citationId);
    }
    for (const clipping of canonical.clippings)
        add(clipping.citationId);
    for (const excerpt of canonical.chatExcerpts)
        add(excerpt.citationId);
    for (const origin of bundle.connected.origins) {
        for (const fact of origin.facts) {
            add(connectedFactCitationId(origin.originSubjectId, fact.citationId));
            for (const evidence of fact.evidence)
                add(evidence.citationId);
        }
        for (const decision of origin.decisions) {
            add(connectedDecisionCitationId(origin.originSubjectId, decision.citationId));
        }
    }
    return ids;
}
export function subjectBundleContentIdentity(bundle) {
    const payload = JSON.stringify({
        bundleVersion: bundle.bundleVersion,
        subjectId: bundle.subjectId,
        truncated: bundle.truncated,
        canonical: bundle.canonical,
        attention: bundle.intelligence.attention,
        changes: bundle.intelligence.changes.map((row) => ({
            id: row.id,
            type: row.type,
            summary: row.summary,
            citationId: row.citationId,
            material: row.material,
        })),
        connected: connectedContentIdentityInput(bundle.connected),
        // A new stance or evidence row changes eligibility, so it must invalidate
        // a cached Current Position (ADR-0117).
        epistemic: bundle.epistemic ?? null,
    });
    return `bundle:${bundle.bundleVersion}:${fnv1aHex(payload)}:${fnv1aHex(payload.slice(Math.floor(payload.length / 2)))}`;
}
export function unresolvedOpenQuestions(bundle) {
    return bundle.canonical.openQuestions.filter((row) => !isOpenQuestionResolved(row.nodeId, bundle.canonical.related));
}
/** Skeleton order: eligible position first, then qualifications, then records. */
const SKELETON_CLASS_ORDER = [
    "self-position",
    "agreed",
    "verified-observation",
    "counterparty-position",
    "recorded-decision",
    "source-reported",
    "derived-inference",
    "proposal",
    "assumption",
    "scenario",
    "rejected-or-contradicted",
    "system-record",
    "recorded-unverified",
    "unclassified-legacy",
];
function competingSentence(entries) {
    const first = entries[0];
    const key = first?.factKey?.replaceAll("_", " ") ?? "value";
    const values = entries
        .map((entry) => entry.proposition.split(" = ").slice(1).join(" = ") || entry.proposition)
        .join(" vs ");
    return {
        text: `Recorded values for ${key} remain competing (${first?.asOf ?? "unknown as-of"}): ${values}. The current value is not settled.`,
        citationIds: entries.map((entry) => entry.citationIds[0] ?? "").filter(Boolean),
    };
}
function latestFirst(a, b) {
    return (b.at ?? "").localeCompare(a.at ?? "");
}
export function buildCurrentPositionSkeleton(bundle, selection = selectSubjectEpistemic(bundle)) {
    const items = [];
    const ident = bundle.canonical.identity;
    const sufficiency = assessCurrentPositionSufficiency(bundle, selection);
    if (bundle.canonical.superseded) {
        items.push({
            text: `${ident.label} is recorded as superseded: ${bundle.canonical.superseded.reason}.`,
            citationIds: [ident.citationId],
        });
    }
    if (sufficiency.status === "insufficient" && sufficiency.reason) {
        items.push({ text: sufficiency.reason, citationIds: [ident.citationId] });
    }
    const open = selection.entries.filter((entry) => entry.class === "open-question");
    // Leave room for the closing "Remains unresolved" line when there is one.
    const cap = CURRENT_POSITION_TARGET_SENTENCES - (open.length > 0 ? 1 : 0);
    const room = () => items.length < cap;
    for (const cls of SKELETON_CLASS_ORDER) {
        const rows = selection.entries.filter((entry) => entry.class === cls);
        if (cls === "recorded-decision") {
            // Latest direct decision, then the latest decision per connected origin.
            const direct = [...rows].filter((row) => !row.originLabel).sort(latestFirst)[0];
            const seenOrigins = new Set();
            const connected = [...rows]
                .filter((row) => row.originLabel)
                .sort(latestFirst)
                .filter((row) => {
                if (seenOrigins.has(row.originLabel ?? ""))
                    return false;
                seenOrigins.add(row.originLabel ?? "");
                return true;
            });
            for (const row of [direct, ...connected]) {
                if (!row || !room())
                    continue;
                items.push({ text: renderEntrySentence(row), citationIds: [...row.citationIds] });
            }
            continue;
        }
        const competingKeys = new Set();
        for (const row of rows) {
            if (!room())
                break;
            if (row.competing && row.factKey && !row.originLabel) {
                if (competingKeys.has(row.factKey))
                    continue;
                competingKeys.add(row.factKey);
                items.push(competingSentence(rows.filter((other) => other.competing && other.factKey === row.factKey)));
                continue;
            }
            items.push({ text: renderEntrySentence(row), citationIds: [...row.citationIds] });
        }
    }
    if (open.length > 0) {
        items.push({
            text: `Remains unresolved: ${open.map((row) => row.proposition).join("; ")}.`,
            citationIds: open.flatMap((row) => row.citationIds),
        });
    }
    if (items.length === 0) {
        items.push({
            text: clip(bundle.canonical.body.text || ident.label, 280),
            citationIds: [
                bundle.canonical.body.text ? bundle.canonical.body.citationId : ident.citationId,
            ],
        });
    }
    return items.slice(0, CURRENT_POSITION_TARGET_SENTENCES);
}
/**
 * Deterministic post-model guard: drop any generated sentence that states its
 * cited records more strongly than their epistemic class allows (ADR-0117).
 */
export function keepEpistemicallyValidItems(items, selection) {
    return items.filter((item) => checkPositionSentence(item.text, item.citationIds, selection).ok);
}
export function keepGroundedCurrentPositionItems(items, allowedCitationIds) {
    return items.filter((item) => {
        if (!item.text.trim() || item.citationIds.length === 0)
            return false;
        return item.citationIds.every((id) => allowedCitationIds.has(id));
    });
}
export function parseCurrentPositionModelOutput(raw) {
    const trimmed = raw.trim();
    const fenced = trimmed.match(/\{[\s\S]*\}/);
    const jsonText = fenced?.[0] ?? trimmed;
    try {
        return CurrentPositionModelOutputSchema.parse(JSON.parse(jsonText));
    }
    catch {
        return null;
    }
}
/**
 * Current Position prompt. The model receives CLASSIFIED records only — never
 * an undifferentiated list of canonical values — plus minimal identity
 * context. Evidence excerpts, associations and overflow are Ask material and
 * are deliberately not part of this prompt.
 */
export function projectSubjectContextForCurrentPosition(bundle, selection = selectSubjectEpistemic(bundle)) {
    const ident = bundle.canonical.identity;
    const identity = [
        "## SUBJECT",
        `[${ident.citationId}] ${ident.label} (kind=${ident.kind})`,
        bundle.canonical.summary.text
            ? `[${bundle.canonical.summary.citationId}] Identity summary (context only): ${clip(bundle.canonical.summary.text, 600)}`
            : null,
        bundle.canonical.superseded ? `SUPERSEDED: ${bundle.canonical.superseded.reason}` : null,
    ]
        .filter(Boolean)
        .join("\n");
    const eligibleClasses = EPISTEMIC_CLASSES.filter((cls) => CURRENT_POSITION_ELIGIBLE_CLASSES.has(cls));
    const otherClasses = EPISTEMIC_CLASSES.filter((cls) => !CURRENT_POSITION_ELIGIBLE_CLASSES.has(cls));
    // Direct records first; connected-origin records keep their own section so
    // origin scoping stays explicit (Phase 6.4 connected contract).
    const direct = {
        ...selection,
        entries: selection.entries.filter((entry) => !entry.originLabel),
    };
    const connected = {
        ...selection,
        entries: selection.entries.filter((entry) => entry.originLabel),
    };
    const eligibleHeading = "## ELIGIBLE PRESENT POSITION (deterministically selected)";
    const eligible = renderEpistemicSections(direct, { classes: eligibleClasses, heading: eligibleHeading }) ||
        `${eligibleHeading}\nNone. No record carries an explicit organisation adoption, agreement, verification or direct counterparty attribution. Do not write any sentence as the organisation's position.`;
    const other = renderEpistemicSections(direct, {
        classes: otherClasses,
        heading: "## OTHER RECORDS (keep each record's frame; never a position)",
    });
    const connectedBlock = renderEpistemicSections(connected, {
        heading: "## CONNECTED KNOWLEDGE (recorded on the named origin Subjects, not on this Subject; classified like any other record)",
    });
    const narrative = bundle.canonical.body.text
        ? `## RECORDED NARRATIVE (unclassified context; not a position)\n[${bundle.canonical.body.citationId}] ${clip(bundle.canonical.body.text, 2_000)}`
        : null;
    return [
        CURRENT_POSITION_DISCIPLINE,
        EPISTEMIC_DISCIPLINE,
        identity,
        eligible,
        ...(other ? [other] : []),
        ...(connectedBlock ? [connectedBlock] : []),
        ...(narrative ? [narrative] : []),
    ].join("\n\n");
}
export function composeCurrentPositionResult(input) {
    const sufficiency = assessCurrentPositionSufficiency(input.bundle, input.selection);
    return CurrentPositionResultSchema.parse({
        status: sufficiency.status,
        origin: input.origin,
        generatedAt: input.generatedAt ?? new Date().toISOString(),
        subjectId: input.bundle.subjectId,
        bundleVersion: 1,
        bundleIdentity: subjectBundleContentIdentity(input.bundle),
        truncated: input.bundle.truncated,
        items: input.items,
        insufficientReason: sufficiency.reason,
    });
}
//# sourceMappingURL=subject-current-position.js.map